import { useMemo } from 'react'
import MuiAlert, { AlertColor } from '@mui/material/Alert'
import MuiSnackbar, { SnackbarOrigin } from '@mui/material/Snackbar'
import cn from 'classnames'
import Icon from '../Icon'
import IconButton from '../IconButton'
import styles from './Snackbar.module.scss'

// Named members for MUI's AlertColor so switches read as `SnackbarType.Success`
// instead of bare 'success'. A const object (not an enum) keeps each value typed
// as the AlertColor literal, so comparing against notification.type (AlertColor)
// shares a type — no unsafe-enum-comparison, no cast.
export const SnackbarType = {
  Success: 'success',
  Info: 'info',
  Warning: 'warning',
  Error: 'error',
} as const satisfies Record<string, AlertColor>

interface IProps {
  notification: {
    isOpen: boolean
    type: AlertColor
    code: string
    message: string
    messagesList: string[]
    anchorOrigin: SnackbarOrigin
    autoHideDuration: number | null
    noShowMessage?: boolean
  }
  onClose: () => void
  onCloseOneMessage: (notification: { messagesList: string[] }) => void
}

const Snackbar = ({ notification, onClose, onCloseOneMessage }: IProps) => {
  const {
    isOpen,
    type,
    message,
    messagesList,
    anchorOrigin,
    autoHideDuration,
    noShowMessage,
    code,
  } = notification

  const isMessageString = useMemo(() => message && typeof message === 'string', [message])

  const getIconByType = (alertType: AlertColor) => {
    switch (alertType) {
      case SnackbarType.Info:
        return <Icon name="info" size="md" />
      case SnackbarType.Warning:
        return <Icon name="alert-triangle" size="md" />
      case SnackbarType.Error:
        return <Icon name="alert-circle" size="md" />
      default:
        return <Icon name="check-circle" size="md" />
    }
  }

  const getClassByType = (alertType: AlertColor) => {
    switch (alertType) {
      case SnackbarType.Success:
        return styles.snackbarSuccess
      case SnackbarType.Info:
        return styles.snackbarInfo
      case SnackbarType.Warning:
        return styles.snackbarWarning
      case SnackbarType.Error:
        return styles.snackbarError
      default:
        return styles.snackbarSuccess
    }
  }

  const getCloseButtonClass = (alertType: AlertColor) => {
    switch (alertType) {
      case SnackbarType.Success:
        return styles.closeButtonSuccess
      case SnackbarType.Info:
        return styles.closeButtonInfo
      case SnackbarType.Warning:
        return styles.closeButtonWarning
      case SnackbarType.Error:
        return styles.closeButtonError
      default:
        return styles.closeButtonSuccess
    }
  }

  const customCloseButton = (onCloseCallback: () => void) => (
    <IconButton
      title="Close"
      size="small"
      onClick={onCloseCallback}
      className={cn(styles.closeButton, getCloseButtonClass(type))}
    >
      <Icon name="x" size="sm" />
    </IconButton>
  )

  return (
    <MuiSnackbar
      open={isOpen}
      anchorOrigin={anchorOrigin}
      autoHideDuration={messagesList?.length > 1 ? null : autoHideDuration}
      onClose={onClose}
    >
      <div>
        {messagesList &&
          messagesList.length > 0 &&
          messagesList.map((messagesListItem) => (
            <MuiAlert
              key={messagesListItem?.replaceAll(' ', '_')}
              className={getClassByType(type)}
              severity={type}
              icon={getIconByType(type)}
              action={customCloseButton(() =>
                onCloseOneMessage({
                  messagesList: messagesList?.filter((el) => el !== messagesListItem),
                }),
              )}
            >
              {messagesListItem}
            </MuiAlert>
          ))}
        {!noShowMessage && message && (
          <MuiAlert
            data-cy="snackbar-success"
            severity={type}
            className={getClassByType(type)}
            variant="filled"
            icon={getIconByType(type)}
            action={customCloseButton(onClose)}
          >
            {isMessageString ? message : code}
          </MuiAlert>
        )}
      </div>
    </MuiSnackbar>
  )
}

export default Snackbar
